import { NavLink } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import { useQuery } from '@/api/query'
import { initials } from '@/lib/format'
import { may, useActiveTenant, useTenants } from '@/tenant/context'
import type { Role } from '@/tenant/context'
import { BudgetMeter } from './BudgetMeter'
import { Lockup } from './Lockup'
import { MODULES, moduleById } from './navigation'
import type { ModuleId } from './navigation'

/**
 * The module switcher.
 *
 * Two buttons rather than a select, because it is a **mode** and both options
 * must be visible at a glance — S-D3's reasoning, and the reason it survives
 * the mobile collapse where ordinary navigation does not.
 */
export function ModuleSwitcher({
  active,
  onChange,
}: {
  active: ModuleId
  onChange: (id: ModuleId) => void
}): JSX.Element {
  return (
    <div className="app-switch" role="group" aria-label="Module">
      {MODULES.map((module) => (
        <button
          key={module.id}
          type="button"
          aria-pressed={module.id === active}
          onClick={() => {
            onChange(module.id)
          }}
        >
          {module.label}
        </button>
      ))}
    </div>
  )
}

/**
 * The section list for one module.
 *
 * `end` on the overview link: `/seo` is a prefix of every future `/seo/*`
 * route, and without it Overview would stay highlighted from inside Keywords.
 */
export function SectionNav({ module }: { module: ModuleId }): JSX.Element {
  const definition = moduleById(module)
  return (
    <nav className="app-nav" aria-label={definition.label}>
      {definition.sections.map((section) =>
        section.to ? (
          <NavLink key={section.label} to={section.to} end={section.to === definition.home}>
            {section.label}
          </NavLink>
        ) : (
          <span key={section.label} aria-disabled="true">
            {section.label}
            <span className="app-nav__soon">Soon</span>
          </span>
        ),
      )}
    </nav>
  )
}

/**
 * The whole sidebar: lockup, switcher, sections, and the organisation card.
 *
 * The card is a button that goes to `/organisations` rather than an inline
 * dropdown. Switching organisation is a *session* write on the server, not a
 * client-side filter — it can fail, and it needs somewhere to say so.
 */
export function Sidebar({
  module,
  onModuleChange,
  onNavigate,
  variant = 'fixed',
}: {
  module: ModuleId
  onModuleChange: (id: ModuleId) => void
  /** Called after any navigation — the mobile drawer closes itself with it. */
  onNavigate?: () => void
  variant?: 'fixed' | 'drawer'
}): JSX.Element {
  const { tenant, role } = useActiveTenant()
  const { memberships } = useTenants()

  return (
    <aside
      className={variant === 'drawer' ? 'app-side app-side--drawer' : 'app-side'}
      onClick={(event) => {
        // One handler rather than a callback threaded through every link: the
        // drawer should close on any navigation inside it, including links a
        // later session adds without knowing this exists.
        if (event.target instanceof HTMLElement && event.target.closest('a')) onNavigate?.()
      }}
    >
      <NavLink className="app-side__lockup" to={moduleById(module).home}>
        <Lockup />
      </NavLink>

      <ModuleSwitcher active={module} onChange={onModuleChange} />
      <SectionNav module={module} />

      <div className="app-side__foot">
        {/* S-B7. Drawn above the organisation card, where `shell#settings` puts
            it, and absent entirely for a tenant with no cap — see BudgetMeter. */}
        <BudgetMeter />
        <NavLink className="app-tenant" to="/organisations">
          <span className="ar-avatar" aria-hidden="true">
            {initials(tenant.name)}
          </span>
          <span className="app-tenant__body">
            <strong>{tenant.name}</strong>
            {/* G22. `billing:read` decides which of the two is mounted — see
                BillingSubline on why this is not a branch inside one. */}
            {may(role, 'billing:read') ? (
              <BillingSubline role={role} organisations={memberships.length} />
            ) : (
              <TenantSubline role={role} organisations={memberships.length} />
            )}
          </span>
        </NavLink>
      </div>
    </aside>
  )
}

/**
 * The second line of the organisation card.
 *
 * The fallback, and what everyone sees until the subscription answers: the role
 * and — only for somebody who has more than one — the organisation count, which
 * is the affordance that says the card is a switcher.
 */
function TenantSubline({
  role,
  organisations,
}: {
  role: Role
  organisations: number
}): JSX.Element {
  return (
    <span>
      {role}
      {organisations > 1 ? ` · ${String(organisations)} organisations` : ''}
    </span>
  )
}

/**
 * G22 — `shell#settings` draws this line as *"Growth · 9 days left"*.
 *
 * **Mounted only for a role that holds `billing:read`.** The subscription is an
 * owner-and-admin read (`app/v1/billing.py`), so for a member this component
 * does not exist and no request is made — rather than one fired on every
 * navigation for the pleasure of a 403. That is why this is a component and not
 * a branch inside `Sidebar`: the query hook has to be unconditional wherever it
 * lives, so the condition is which component gets mounted.
 *
 * **Every unusable answer falls back rather than blanking**, following
 * `BudgetMeter`: loading, a failed request, no subscription at all. A sidebar is
 * not where a failed request should be reported, and a skeleton in the foot of
 * every page is a flicker on every navigation.
 *
 * *"9 days left"* is shown only while the status is actually `trialing` — the
 * same condition `BillingPage` gates its *Trial ends* row on. `trial_ends_at`
 * outlives the trial on a converted subscription, so reading the date alone
 * would go on counting down days for a tenant who is paying.
 *
 * **Two segments, never three**, which is why the organisation count is the
 * fallback's and not this line's. Measured in a browser at the real 17rem rail:
 * *"Growth · 9 days left"* is 113px and every plan name fits on one line, while
 * appending *"· 2 organisations"* wraps to two and grows the card from 73px to
 * 100px. `.app-tenant__body span` has no `text-overflow`, unlike the `strong`
 * above it, so a long line wraps rather than clipping — and the card is a link
 * to the switcher whether or not it prints a count.
 */
function BillingSubline({
  role,
  organisations,
}: {
  role: Role
  organisations: number
}): JSX.Element {
  const subscription = useQuery(
    (signal) => call(() => api.GET('/api/v1/billing/subscription', { signal })),
    [],
  )

  if (subscription.state.status !== 'ready') {
    return <TenantSubline role={role} organisations={organisations} />
  }
  const current = subscription.state.data.subscription
  if (!current) return <TenantSubline role={role} organisations={organisations} />

  const days = current.status === 'trialing' ? trialDaysLeft(current.trial_ends_at) : null
  return (
    <span>
      {current.plan_name}
      {days === null ? '' : ` · ${days === 1 ? '1 day' : `${String(days)} days`} left`}
    </span>
  )
}

/**
 * Whole days from now until a trial ends, or `null` when there is no useful
 * number to print.
 *
 * Rounded **up**, because a trial with eleven hours to run is a trial with a day
 * left rather than none — and rounding down would print *"0 days left"* to a
 * tenant who still has the evening. An expired or missing date returns `null`
 * rather than a negative: past the end there is no countdown to state, and the
 * status will have moved off `trialing` anyway.
 */
function trialDaysLeft(endsAt: string | null | undefined): number | null {
  if (!endsAt) return null
  const remaining = Date.parse(endsAt) - Date.now()
  if (Number.isNaN(remaining) || remaining <= 0) return null
  return Math.ceil(remaining / 86_400_000)
}
